import { z } from "zod";

export const FORECAST_HORIZON_HOURS = 168;
export const FORECAST_MAX_AGE_MS = 2 * 60 * 60 * 1000;
export const forecastCriteriaSchema = z.object({
  minWaveHeight: z.number().finite().min(0).max(50),
  maxWindSpeed: z.number().finite().min(0).max(150),
  leadHoursMin: z.number().int().min(1).max(167),
  leadHoursMax: z.number().int().min(1).max(168),
}).refine(t => t.leadHoursMax >= t.leadHoursMin, "End of window must follow start");
export type ForecastCriteria = z.infer<typeof forecastCriteriaSchema>;
export interface ForecastHour {
  validAt: string;
  waveHeightFt: number | null;
  wavePeriodSec: number | null;
  waveDirectionDeg: number | null;
  windSpeedMph: number | null;
  windDirectionDeg: number | null;
}
export interface ForecastRun {
  kind: "model";
  source: "Open-Meteo Marine";
  windSource: "Open-Meteo Weather";
  fetchedAt: string;
  modelIssuedAt: null; // Provider does not expose the model's issuance timestamp.
  timezone: string;
  latitude: number;
  longitude: number;
  marineLatitude: number;
  marineLongitude: number;
  synthetic: false;
  hours: ForecastHour[];
}
export function trustedHour(run: ForecastRun, hour: ForecastHour, now = Date.now()): boolean {
  const fetched = Date.parse(run.fetchedAt);
  const valid = Date.parse(hour.validAt);
  return run.kind === "model" && run.synthetic === false &&
    run.source === "Open-Meteo Marine" && run.windSource === "Open-Meteo Weather" &&
    Number.isFinite(fetched) && fetched <= now && now - fetched <= FORECAST_MAX_AGE_MS &&
    Number.isFinite(valid) && valid > now && valid <= fetched + FORECAST_HORIZON_HOURS * 3_600_000 &&
    hour.waveHeightFt !== null && Number.isFinite(hour.waveHeightFt) && hour.waveHeightFt >= 0 &&
    hour.windSpeedMph !== null && Number.isFinite(hour.windSpeedMph) && hour.windSpeedMph >= 0;
}
export function matchingForecast(run: ForecastRun, criteria: ForecastCriteria, now = Date.now()) {
  if (!forecastCriteriaSchema.safeParse(criteria).success) return undefined;
  return run.hours.find(hour => {
    const lead = (Date.parse(hour.validAt) - now) / 3_600_000;
    return trustedHour(run, hour, now) && lead >= criteria.leadHoursMin &&
      lead <= criteria.leadHoursMax && hour.waveHeightFt! >= criteria.minWaveHeight &&
      hour.windSpeedMph! <= criteria.maxWindSpeed;
  });
}
export function localDate(at: string | number | Date, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(new Date(at));
}
export function calendarDay(date: string, offset: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}
/** Tomorrow is a calendar date in the alert's timezone, not the next 24 hours. */
export function tomorrowForecast(run: ForecastRun, timezone = run.timezone, now = Date.now()): ForecastRun {
  const tomorrow = calendarDay(localDate(now, timezone), 1);
  return { ...run, timezone, hours: run.hours.filter(hour =>
    Number.isFinite(Date.parse(hour.validAt)) && localDate(hour.validAt, timezone) === tomorrow) };
}
export function matchingTomorrowForecast(run: ForecastRun, criteria: ForecastCriteria, timezone = run.timezone, now = Date.now()) {
  if (!forecastCriteriaSchema.safeParse(criteria).success) return undefined;
  // Legacy saved lead-time settings do not restrict or expand tomorrow.
  return tomorrowForecast(run, timezone, now).hours.find(hour =>
    trustedHour(run, hour, now) && hour.waveHeightFt! >= criteria.minWaveHeight &&
    hour.windSpeedMph! <= criteria.maxWindSpeed);
}
export function horizonLabel(leadHours: number): string {
  return leadHours <= 48 ? "Near-term model outlook" : leadHours <= 96
    ? "Planning outlook — may change" : "Longer-range outlook — more uncertain";
}
